import React, { useState, useCallback, useEffect, useMemo } from "react";
import * as Yup from "yup";
import { Formik, Form as FormikForm, useField, useFormikContext } from "formik";
import {
  Form,
  Search,
  Grid,
  Button,
  Label,
  Icon,
  Divider,
  Segment,
  Message,
  type SearchProps,
} from "semantic-ui-react";
import axios from "../../../apis/dvdlocator";
import { debounce } from "lodash";
import { fetchUserAttributes } from "@aws-amplify/auth";
import "./index.css";
import type { ProductData } from "./AddSupplyUnitWizard/types";

interface ProductFormProps {
  nextPage: () => void;
  product: ProductData;
  setProduct: (product: ProductData) => void;
  onClose: () => void;
  preselectedSupplyUnits: any[];
}

const PRODUCT_CONTACT_OPTIONS = [
  {
    key: "Patrick Klotzbach",
    value: "Patrick Klotzbach",
    text: "Patrick Klotzbach",
  },
  { key: "Robyn Baun", value: "Robyn Baun", text: "Robyn Baun" },
  { key: "Jonathan Briggs", value: "Jonathan Briggs", text: "Jonathan Briggs" },
  { key: "Alex Burns", value: "Alex Burns", text: "Alex Burns" },
  { key: "Kayla Winter", value: "Kayla Winter", text: "Kayla Winter" },
  {
    key: "Marissa Carbonell",
    value: "Marissa Carbonell",
    text: "Marissa Carbonell",
  },
];

const FormikField = ({
  label,
  formSubmitted,
  name,
  ...props
}: {
  label: string;
  formSubmitted: boolean;
  name: string;
  [key: string]: any;
}) => {
  const [field, meta] = useField(name);
  return (
    <Form.Field>
      <label>{label}</label>
      <Form.Input {...field} {...props} />
      {formSubmitted && meta.error ? (
        <Label basic color="red" pointing>
          {meta.error}
        </Label>
      ) : null}
    </Form.Field>
  );
};

function AmplifyUserSync() {
  const { setFieldValue, values } = useFormikContext<any>();

  useEffect(() => {
    if (values.createdBy) return;
    fetchUserAttributes()
      .then((attrs: any) => {
        const name = attrs.name;
        setFieldValue("createdBy", name, false);
      })
      .catch(() => setFieldValue("createdBy", "Unknown", false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

const validationSchema = Yup.object({
  title: Yup.string().min(8).max(250).required("Title is required"),
  createdOn: Yup.string().required(),
  createdBy: Yup.string().required(
    "Created By could not be loaded. Please refresh and try again."
  ),
  productContact: Yup.string().required("Product Contact is required"),
});

export function ProductForm({
  nextPage,
  product,
  setProduct,
  onClose,
  preselectedSupplyUnits,
}: ProductFormProps) {
  const initialCreatedOn = useMemo(
    () => product.createdOn || new Date().toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [searchLoading, setSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<
    { id: number; title: string }[]
  >([]);
  const [searchError, setSearchError] = useState("");

  const debouncedSearch = useCallback(
    debounce(async (value: string) => {
      if (!value) {
        setSearchResults([]);
        setSearchError("");
        return;
      }
      setSearchLoading(true);
      setSearchError("");
      try {
        const result = await axios.get(`/families?name=${value}`);
        setSearchResults(
          result.data.map((e: any) => ({ id: e.id, title: e.family }))
        );
      } catch (err: any) {
        setSearchError(
          err.response?.data?.message ||
            "An error occurred while searching. Please try again."
        );
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500),
    []
  );

  const handleSearchChange = (_: any, data: SearchProps) => {
    const value = data.value ?? "";
    setSearchValue(value);
    debouncedSearch(value);
  };

  const customResultRenderer = (
    result: { id: number; title: string },
    families: { id: number; title: string }[]
  ) => {
    const isSelected = families.some((f) => f.title === result.title);
    return (
      <div
        style={{
          opacity: isSelected ? 0.5 : 1,
          cursor: isSelected ? "not-allowed" : "pointer",
          padding: "8px",
          color: isSelected ? "#666" : "inherit",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{result.title}</span>
          {isSelected && (
            <span style={{ fontSize: "0.8em", color: "#666" }}>
              (Already Added)
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Formik
      initialValues={{
        ...product,
        createdOn: initialCreatedOn,
      }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        setProduct({
          title: values.title,
          partNumber: values.partNumber,
          families: values.families,
          product_id: values.product_id,
          createdOn: values.createdOn,
          createdBy: values.createdBy,
          productContact: values.productContact,
        });
        setSubmitting(false);
        nextPage();
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        submitCount,
        values,
        setFieldValue,
        errors,
      }) => {
        const handleResultSelect = (_: any, { result }: any) => {
          if (values.families.some((f) => f.title === result.title)) return;
          setFieldValue("families", [...values.families, result]);
          setSearchValue("");
        };

        const handleRemoveFamily = (familyId: number) => {
          setFieldValue(
            "families",
            values.families.filter((f) => f.id !== familyId)
          );
        };

        return (
          <Form as={FormikForm} onSubmit={handleSubmit}>
            <AmplifyUserSync />
            <Segment basic>
              <FormikField
                label="Title"
                name="title"
                placeholder="Enter Title"
                formSubmitted={submitCount > 0}
              />
              <Form.Field>
                <label>Created On</label>
                <Form.Input
                  readOnly
                  value={
                    values.createdOn
                      ? new Date(values.createdOn).toLocaleString()
                      : ""
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Created By</label>
                <Form.Input readOnly value={values.createdBy || "Loading..."} />
              </Form.Field>
              <Form.Field required>
                <label>Product Contact</label>
                <Form.Select
                  options={PRODUCT_CONTACT_OPTIONS}
                  value={values.productContact}
                  onChange={(_, data) =>
                    setFieldValue("productContact", data.value)
                  }
                  placeholder="Select Product Contact"
                />
                {submitCount > 0 && errors.productContact && (
                  <Label basic color="red" pointing>
                    {errors.productContact as string}
                  </Label>
                )}
              </Form.Field>
              <Form.Field>
                <label>Families</label>
                <Search
                  fluid
                  input={{ fluid: true }}
                  loading={searchLoading}
                  placeholder="Search Families"
                  onResultSelect={handleResultSelect}
                  onSearchChange={handleSearchChange}
                  results={searchResults}
                  resultRenderer={(result: any) =>
                    customResultRenderer(result, values.families)
                  }
                  minCharacters={2}
                  value={searchValue}
                  className="scrolling-search"
                />
                {searchError && (
                  <Message
                    error
                    content={searchError}
                    style={{ marginTop: "5px" }}
                  />
                )}
                <div style={{ marginTop: "10px" }}>
                  {values.families.map((family) => (
                    <Label
                      key={family.id}
                      style={{ marginRight: "5px", marginBottom: "5px" }}
                    >
                      {family.title}
                      <Icon
                        name="delete"
                        onClick={() => handleRemoveFamily(family.id)}
                      />
                    </Label>
                  ))}
                </div>
              </Form.Field>
            </Segment>

            <Divider />
            <Segment basic>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Button type="button" onClick={onClose} floated="left">
                      Cancel
                    </Button>
                  </Grid.Column>
                  <Grid.Column width={8} textAlign="right">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      {preselectedSupplyUnits.length > 0 && (
                        <Label
                          color="grey"
                          style={{ marginRight: "10px" }}
                          size="large"
                        >
                          {`${preselectedSupplyUnits.length} supply unit(s) preselected`}
                        </Label>
                      )}
                      <Button type="submit" primary disabled={isSubmitting}>
                        Next
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          </Form>
        );
      }}
    </Formik>
  );
}
